import type { BodensteckbriefEntry, BodensteckbriefFbfRow, FeinbodenformEntry } from './listen.service';

/**
 * Ableitung der Feinbodenformen-Liste aus dem Hauptbodenformenkatalog
 * (Bodensteckbriefe).
 *
 * Hintergrund / Beschluss (2026-06-15):
 *  - Die Feinbodenformen-Liste leitet ihre Kernfelder (Name, FBF-Code, HBF,
 *    HBF-lang, Naehrkraft, Seite, Serie, Substratgruppe) READ-ONLY aus dem
 *    Katalog ab.
 *  - Eigene, editierbare Felder bleiben: `zuordnung` (Wasserform -> STFG) und
 *    `Hw` (Hinweis). Diese werden ueber den FBF-Code an die abgeleiteten Felder
 *    angeheftet ("Extras").
 *  - Der Katalog ist MASSGEBEND. Jede Abweichung der abgeleiteten Werte vom
 *    bisherigen Listenwert wird protokolliert (ChangeLogEntry), NICHT still
 *    ueberschrieben.
 *  - Umfang: alle Katalog-FBFs erscheinen; FBFs, die nur in der alten Liste
 *    existieren (kein Katalog-Treffer), bleiben als manuelle Eintraege erhalten.
 *
 * Reines Modul ohne Angular-/DB-Abhaengigkeiten (nur `import type`), damit es
 * isoliert unter vitest testbar ist und sowohl im Service als auch in einem
 * Report-Skript wiederverwendet werden kann.
 */

/** Editierbare Zusatzinfos einer FBF, die NICHT aus dem Katalog stammen. */
export interface FeinbodenformExtras {
  zuordnung?: Record<string, string>;
  Hw?: string | null;
}

/** Abgeleiteter Eintrag inkl. (optionalem) name_lang-Cache. */
export type DerivedFeinbodenform = FeinbodenformEntry & { name_lang?: string };

/** Ein protokollierter Feldwechsel beim Ableiten (alt -> neu). */
export interface ChangeLogEntry {
  code: string;
  field: 'name' | 'FBF_display' | 'HBF' | 'HBF_lang' | 'NKr' | 'Seite' | 'Serie';
  old: string;
  neu: string;
}

/** Katalog-FBF-Zeile, die NICHT abgeleitet wird (keine gueltige Abkuerzung). */
export interface SkippedFbf {
  steckbrief: string;
  lokalname: string;
  suffix: string;
  /** 'artifact' = Parser-Kopfzeile (lokalname "Abkürzung"); 'no-abbreviation' =
   *  echte/teil-Bezeichnung ohne Abkuerzung (Sonder-/Querverweis). */
  reason: 'artifact' | 'no-abbreviation';
}

export interface DeriveResult {
  /** Vollstaendige FBF-Liste: Katalog-abgeleitet (Katalogreihenfolge) +
   *  manuelle Waisen am Ende. */
  list: DerivedFeinbodenform[];
  /** Protokoll aller Wertaenderungen gegenueber der alten Liste. */
  changes: ChangeLogEntry[];
  /** FBF-Codes, die im Katalog MEHRFACH vorkommen (mehrdeutiger Master). */
  duplicates: { code: string; steckbriefe: string[] }[];
  /** Uebersprungene Katalog-Zeilen (keine Abkuerzung -> kein gueltiger Code). */
  skipped: SkippedFbf[];
  /** FBF-Codes, die nur in der alten Liste stehen (bleiben manuell). */
  manualOnly: string[];
  /** FBF-Codes, die neu aus dem Katalog hinzukommen (kein alter Eintrag). */
  added: string[];
}

// ===================== Feld-Ableitungen (Katalog -> Liste) =====================

const trim = (s: unknown): string => (s == null ? '' : String(s)).trim();
const compact = (s: unknown): string => trim(s).replace(/\s+/g, '');

/** Voller FBF-Code = FBF-Abkuerzung + Klassensuffix des Steckbriefs, ohne
 *  Leerzeichen (Join-Schluessel), z.B. "Mz" + "LG" = "MzLG". */
export function derivedCode(abkuerzung: string | undefined, suffix: string | undefined): string {
  return compact(abkuerzung) + compact(suffix);
}

/** Anzeige-Variante mit Leerzeichen, z.B. "Mz LG". */
export function derivedDisplay(abkuerzung: string | undefined, suffix: string | undefined): string {
  return (trim(abkuerzung) + ' ' + trim(suffix)).trim();
}

/** Naehrkraft = nahrkraftstufe + (funftelstufe ? " " + funftelstufe : ""). */
export function derivedNKr(nahrkraftstufe: unknown, funftelstufe: unknown): string {
  const n = trim(nahrkraftstufe);
  const f = trim(funftelstufe);
  return f ? `${n} ${f}` : n;
}

/** name_lang = Lokalname + " " + HBF-Langname (Cache, wie in der Altliste). */
export function derivedNameLang(lokalname: string | undefined, hbfLang: string | undefined): string {
  return [trim(lokalname), trim(hbfLang)].filter(Boolean).join(' ');
}

// ============================ Serie-Extraktion ============================
//
// Serie steckt in der Schraffurtabelle: schraffierte ("haken"/"x") Zellen unter
// einer Serie-Spalte bedeuten "diese Serie trifft fuer die FBF zu". Eine
// Spaltenueberschrift kann mehrere Serien umfassen ("II, IV, VI"). Encoding ist
// uneinheitlich:
//   - `schraffuren` ist mal ein Objekt {colId: 'haken'} mal ein Array [colId].
//   - Serie-Spalten tragen mal "...Serie / <roem>" als Kopf, mal nur "<roem>".
//   - Vereinzelt steht die Serie als Freitext direkt in der Zelle.
// Wir extrahieren die MENGE der zutreffenden Serien und formatieren sie
// kanonisch. Wo der Katalog keine Serie kodiert, ist das Ergebnis leer (das
// wird beim Ableiten protokolliert).

const ROMAN: Record<string, number> = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8 };
const ROMAN_REV = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];
// Reihenfolge der VIII-Modifikatoren in der kanonischen Ausgabe.
const VIII_MOD_ORDER = ['+', 'o', '-'];

/** Ein Label sieht "serienhaft" aus, wenn es mit einer roemischen Ziffer (I..VIII)
 *  beginnt – optional gefolgt von Modifikatoren/Aufzaehlung. */
function looksLikeSerieLabel(label: string): boolean {
  return /^(VIII|VII|VI|V|IV|III|II|I)\b/.test(label.trim());
}

/**
 * Ist die Spalte (anhand ihrer Ebenen-Koepfe) eine Serie-Spalte? Liefert das
 * Serie-Label (z.B. "II, IV, VI" oder "VIII +").
 *  - Kopf enthaelt das Wort "Serie": Label = Ebenen NACH der Serie-Ebene.
 *  - Sonst: letzte Ebene, falls sie serienhaft aussieht.
 */
export function serieLabelOf(ebenen: (string | undefined)[] | undefined): string | null {
  const eb = (ebenen || []).map(x => trim(x)).filter(x => x.length > 0);
  if (!eb.length) return null;
  const gi = eb.findIndex(x => /serie/i.test(x));
  if (gi >= 0) {
    const after = eb.slice(gi + 1).join(' ').trim();
    return after || ''; // "...Serie" ohne Sublabel = Serie-Spalte ohne konkrete Ziffer
  }
  const last = eb[eb.length - 1];
  return looksLikeSerieLabel(last) ? last : null;
}

/** Schraffierte Spalten-IDs (haken/x) + Freitext-Zellen ({colId: text}). */
export function hatchedColumns(schraffuren: unknown): { hatched: Set<string>; freetext: Map<string, string> } {
  const hatched = new Set<string>();
  const freetext = new Map<string, string>();
  if (Array.isArray(schraffuren)) {
    for (const c of schraffuren) hatched.add(String(c));
  } else if (schraffuren && typeof schraffuren === 'object') {
    for (const [c, v] of Object.entries(schraffuren as Record<string, unknown>)) {
      const val = trim(v);
      if (val === 'haken' || val.toLowerCase() === 'x') hatched.add(String(c));
      else if (val) freetext.set(String(c), val);
    }
  }
  return { hatched, freetext };
}

/** Zerlegt ein Serie-Label in einzelne "Atome": plain roemisch (I..VIII) oder
 *  VIII mit Modifikator (+, 0/o, -). "II, IV, VI" -> ['II','IV','VI'];
 *  "VIII +" -> ['VIII+']; "II – VI" (Bereich) -> ['II','III','IV','V','VI']. */
export function parseSerieAtoms(label: string): string[] {
  const s = trim(label);
  if (!s) return [];
  const tokens = s.match(/VIII\s*[+0o-]?|VII|VI|V|IV|III|II|I/g) || [];
  const atoms: string[] = [];
  for (const t of tokens) {
    const m = /^(VIII)\s*([+0o-])?$/.exec(t);
    if (m) {
      const mod = m[2] ? (m[2] === '0' ? 'o' : m[2]) : '';
      atoms.push('VIII' + mod);
    } else {
      atoms.push(t);
    }
  }
  // Bereich "X – Y" (Gedankenstrich/Bindestrich zwischen genau zwei plain-Ziffern)
  // auffuellen: II – VI -> II,III,IV,V,VI.
  const plain = atoms.filter(a => ROMAN[a] != null);
  if (/[–—-]/.test(s) && plain.length === 2 && atoms.length === 2) {
    const a = ROMAN[plain[0]];
    const b = ROMAN[plain[1]];
    if (a != null && b != null) {
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      const out: string[] = [];
      for (let i = lo; i <= hi; i++) out.push(ROMAN_REV[i]);
      return out;
    }
  }
  return atoms;
}

/** Formatiert eine Atom-Menge kanonisch:
 *  - plain Ziffern sortiert; zusammenhaengende Laeufe (>=3) als "II – VI",
 *    sonst kommagetrennt;
 *  - VIII-Modifikatoren zu "VIII + o -" zusammengezogen;
 *  - Reihenfolge: plain-Block, dann VIII-Modifikatoren. */
export function formatSerie(atoms: Iterable<string>): string {
  const set = new Set(atoms);
  const plainNums = [...set].filter(a => ROMAN[a] != null).map(a => ROMAN[a]).sort((x, y) => x - y);
  const vmods = VIII_MOD_ORDER.filter(m => set.has('VIII' + m));

  const parts: string[] = [];
  // plain Ziffern in Laeufe zerlegen
  let i = 0;
  while (i < plainNums.length) {
    let j = i;
    while (j + 1 < plainNums.length && plainNums[j + 1] === plainNums[j] + 1) j++;
    const runLen = j - i + 1;
    if (runLen >= 3) {
      parts.push(`${ROMAN_REV[plainNums[i]]} – ${ROMAN_REV[plainNums[j]]}`);
    } else {
      for (let k = i; k <= j; k++) parts.push(ROMAN_REV[plainNums[k]]);
    }
    i = j + 1;
  }
  let out = parts.join(', ');
  if (vmods.length) {
    const viii = 'VIII ' + vmods.join(' ');
    out = out ? `${out}, ${viii}` : viii;
  }
  return out;
}

/** Voller Serie-Wert einer FBF aus ihrem Steckbrief (kann leer sein). */
export function extractSerie(steckbrief: BodensteckbriefEntry, fbf: BodensteckbriefFbfRow): string {
  const spalten = steckbrief.spalten || {};
  // Serie-Label je Spalten-ID vorberechnen.
  const serieLabelByCol = new Map<string, string>();
  for (const [col, sp] of Object.entries(spalten)) {
    const label = serieLabelOf(sp?.ebenen);
    if (label != null) serieLabelByCol.set(String(col), label);
  }
  if (!serieLabelByCol.size) return '';

  const { hatched, freetext } = hatchedColumns(fbf.schraffuren);
  const atoms = new Set<string>();
  // 1) schraffierte Serie-Spalten -> Label-Atome
  for (const col of hatched) {
    const label = serieLabelByCol.get(col);
    if (label) parseSerieAtoms(label).forEach(a => atoms.add(a));
  }
  // 2) Freitext in einer Serie-Spalte -> der Freitext IST das Serie-Label
  for (const [col, text] of freetext) {
    if (serieLabelByCol.has(col)) parseSerieAtoms(text).forEach(a => atoms.add(a));
  }
  return formatSerie(atoms);
}

// ============================ Komposition / Diff ============================

interface CatalogFbf {
  code: string;
  display: string;
  name: string;
  nkr: string;
  serie: string;
  hbf: string;
  hbfLang: string;
  /** Substratgruppe (S, bS, lS, G, …): steht im Katalog an der HAUPTbodenform
   *  und gilt damit fuer alle Feinbodenformen desselben Steckbriefs. */
  stg: string;
  seite: string;
  nameLang: string;
  gestrichen: boolean;
  steckbrief: string;
}

/** Indiziert alle Katalog-FBFs auf ihren vollen Code; meldet Dubletten. */
export function buildCatalogIndex(catalog: BodensteckbriefEntry[]): {
  byCode: Map<string, CatalogFbf>;
  order: string[];
  duplicates: { code: string; steckbriefe: string[] }[];
  skipped: SkippedFbf[];
} {
  const byCode = new Map<string, CatalogFbf>();
  const order: string[] = [];
  const dupeMap = new Map<string, string[]>();
  const skipped: SkippedFbf[] = [];
  for (const s of catalog || []) {
    const suffix = s.fbf_klassensuffix || '';
    const hbf = s.hauptbodenform || {};
    for (const f of s.feinbodenformen || []) {
      // Eine FBF braucht eine Abkuerzung, um einen eindeutigen Code zu bilden.
      // Abkuerzungslose Zeilen sind entweder Parser-Artefakte (Kopfzeile
      // "Abkürzung") oder Sonder-/Querverweise - beide werden uebersprungen
      // und transparent gemeldet (statt auf den Suffix-only-Code zu kollidieren).
      if (!compact(f.abkuerzung)) {
        skipped.push({
          steckbrief: trim(s.id),
          lokalname: trim(f.lokalname),
          suffix: trim(suffix),
          reason: trim(f.lokalname) === 'Abkürzung' ? 'artifact' : 'no-abbreviation',
        });
        continue;
      }
      const code = derivedCode(f.abkuerzung, suffix);
      if (!code) continue;
      const rec: CatalogFbf = {
        code,
        display: derivedDisplay(f.abkuerzung, suffix),
        name: trim(f.lokalname),
        nkr: derivedNKr(f.nahrkraftstufe, f.funftelstufe),
        serie: extractSerie(s, f),
        hbf: trim(hbf.abkuerzung),
        hbfLang: trim(hbf.name),
        stg: trim(hbf.substratgruppe),
        seite: trim(s.seitenzahl),
        nameLang: derivedNameLang(f.lokalname, hbf.name),
        gestrichen: !!f.gestrichen,
        steckbrief: trim(s.id),
      };
      if (byCode.has(code)) {
        const first = byCode.get(code)!;
        const list = dupeMap.get(code) || [first.steckbrief];
        list.push(rec.steckbrief);
        dupeMap.set(code, list);
        // Ersten Treffer als Master behalten (deterministisch).
      } else {
        byCode.set(code, rec);
        order.push(code);
      }
    }
  }
  const duplicates = [...dupeMap.entries()].map(([code, steckbriefe]) => ({ code, steckbriefe }));
  return { byCode, order, duplicates, skipped };
}

/** Liest die editierbaren Extras (zuordnung, Hw) aus einem (alten oder bereits
 *  extras-only) Listeneintrag. */
export function extrasOf(entry: Partial<FeinbodenformEntry> | undefined): FeinbodenformExtras {
  if (!entry) return {};
  const out: FeinbodenformExtras = {};
  if (entry.zuordnung && typeof entry.zuordnung === 'object') out.zuordnung = { ...entry.zuordnung };
  if (entry.Hw != null && trim(entry.Hw) !== '') out.Hw = entry.Hw;
  return out;
}

// Vergleich fuer das Protokoll: Serie semantisch (Atom-Menge), sonst getrimmt.
const serieKey = (s: string): string => [...new Set(parseSerieAtoms(s))].sort().join('|');

/**
 * Baut die abgeleitete FBF-Liste aus Katalog + alter Liste und protokolliert
 * jede Wertaenderung der abgeleiteten Felder.
 */
export function deriveFeinbodenformen(
  catalog: BodensteckbriefEntry[],
  legacyList: FeinbodenformEntry[],
): DeriveResult {
  const { byCode, order, duplicates, skipped } = buildCatalogIndex(catalog);
  const legacyByCode = new Map<string, FeinbodenformEntry>();
  for (const e of legacyList || []) legacyByCode.set(compact(e.FBF), e);

  const list: DerivedFeinbodenform[] = [];
  const changes: ChangeLogEntry[] = [];
  const added: string[] = [];

  for (const code of order) {
    const c = byCode.get(code)!;
    const legacy = legacyByCode.get(code);
    const extras = extrasOf(legacy);
    if (!legacy) added.push(code);

    const entry: DerivedFeinbodenform = {
      FBF: c.code,
      FBF_display: c.display,
      name: c.name,
      name_lang: c.nameLang,
      HBF: c.hbf,
      HBF_lang: c.hbfLang,
      // Neu abgeleitetes Feld (11.09.2026). Es steht in KEINER Altliste, deshalb
      // wird es auch nicht protokolliert. Sonst stuende fuer jede einzelne FBF
      // ein "Wert geaendert: '' -> 'S'" im Bericht, ohne dass sich etwas
      // geaendert haette.
      STG: c.stg,
      NKr: c.nkr,
      Serie: c.serie || null, // abgeleitet (read-only), kein Extra
      Hw: extras.Hw ?? null,
      zuordnung: extras.zuordnung ?? {},
      Seite: c.seite,
    };

    list.push(entry);

    // Protokoll: nur wenn es einen alten Eintrag gab und sich der Wert aendert.
    if (legacy) {
      const log = (field: ChangeLogEntry['field'], oldVal: unknown, neuVal: unknown, eq?: (a: string, b: string) => boolean) => {
        const o = trim(oldVal);
        const n = trim(neuVal);
        const same = eq ? eq(o, n) : o === n;
        if (!same) changes.push({ code, field, old: o, neu: n });
      };
      log('name', legacy.name, c.name);
      log('FBF_display', legacy.FBF_display, c.display);
      log('HBF', legacy.HBF, c.hbf);
      log('HBF_lang', legacy.HBF_lang, c.hbfLang);
      log('NKr', legacy.NKr, c.nkr, (a, b) => a.replace(/\s+/g, '') === b.replace(/\s+/g, ''));
      log('Seite', legacy.Seite, c.seite);
      log('Serie', legacy.Serie, c.serie, (a, b) => serieKey(a) === serieKey(b));
    }
  }

  // Manuelle Waisen: nur in der Altliste vorhanden -> unveraendert behalten.
  const manualOnly: string[] = [];
  for (const e of legacyList || []) {
    const code = compact(e.FBF);
    if (!byCode.has(code)) {
      manualOnly.push(code);
      list.push({ ...e });
    }
  }

  return { list, changes, duplicates, skipped, manualOnly, added };
}

/**
 * Reduziert eine (abgeleitete) FBF-Liste auf die SPEICHERFORM:
 *  - Katalog-FBFs: nur dann ablegen, wenn sie eigene Zusatzinfos tragen
 *    (zuordnung/Hw) - und dann auch nur Code + diese Felder. Die abgeleiteten
 *    Felder kommen beim Lesen wieder live aus dem Katalog.
 *  - Manuelle Waisen (kein Katalog-Treffer): vollstaendig behalten.
 * So bleibt das gespeicherte Dokument schlank und kann nicht mit dem Katalog
 * driften.
 */
export function toStoredFeinbodenformen(
  list: Partial<FeinbodenformEntry>[],
  catalog: BodensteckbriefEntry[],
): Array<Partial<FeinbodenformEntry>> {
  const { byCode } = buildCatalogIndex(catalog);
  const out: Array<Partial<FeinbodenformEntry>> = [];
  for (const e of list || []) {
    const code = compact(e.FBF);
    if (byCode.has(code)) {
      const ex = extrasOf(e);
      const hasZuordnung = ex.zuordnung && Object.keys(ex.zuordnung).length > 0;
      const hasHw = ex.Hw != null && trim(ex.Hw) !== '';
      if (!hasZuordnung && !hasHw) continue; // keine Extras -> nicht speichern
      const lean: Partial<FeinbodenformEntry> = { FBF: e.FBF };
      if (hasZuordnung) lean.zuordnung = ex.zuordnung;
      if (hasHw) lean.Hw = ex.Hw;
      out.push(lean);
    } else {
      out.push({ ...e }); // manuelle Waise vollstaendig
    }
  }
  return out;
}
